'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

interface ContactForm {
  name: string; email: string; phone: string; subject: string; message: string;
}

export default function ContactPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const kn = language === 'kn';

  const [form, setForm] = useState<ContactForm>({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<ContactForm>>({});

  const validate = () => {
    const e: Partial<ContactForm> = {};
    if (!form.name.trim()) e.name = kn ? 'ಹೆಸರು ಅಗತ್ಯ' : 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = kn ? 'ಮಾನ್ಯ ಇಮೇಲ್ ಅಗತ್ಯ' : 'Valid email required';
    if (!form.message.trim()) e.message = kn ? 'ಸಂದೇಶ ಅಗತ್ಯ' : 'Message is required';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitted(true);
  };

  const contactItems = [
    { icon: '📍', label: t.contact.address, value: 'Arivu Foundation\nBengaluru, Karnataka 560073\nIndia' },
    { icon: '📞', label: t.contact.phone, value: '+91 7353101553' },
    { icon: '✉️', label: t.contact.emailLabel, value: 'info@arivufoundation.org' },
    { icon: '🕐', label: t.contact.officeHours, value: kn ? 'ಸೋಮ–ಶನಿ: ಬೆಳಿಗ್ಗೆ 9:00 – ಸಂಜೆ 6:00\nಭಾನುವಾರ: ಮುಚ್ಚಿರುತ್ತದೆ' : 'Mon–Sat: 9:00 AM – 6:00 PM\nSunday: Closed' },
  ];

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <main className="pt-24">
        <section className="hero-gradient py-20 px-6 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className={`text-4xl sm:text-5xl font-bold mb-4 ${kn ? 'font-kannada' : ''}`}>
              {t.contact.heroTitle} <span className="text-saffron-light">{t.contact.heroHighlight}</span>
            </h1>
            <p className={`text-white/75 text-lg max-w-xl mx-auto ${kn ? 'font-kannada' : ''}`}>{t.contact.heroSubtitle}</p>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className={`text-2xl font-bold text-indigo mb-8 ${kn ? 'font-kannada' : ''}`}>{t.contact.getInTouch}</h2>
              <div className="space-y-6">
                {contactItems.map((item) => (
                  <div key={item.label} className="flex gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-saffron-pale flex items-center justify-center text-2xl flex-shrink-0">{item.icon}</div>
                    <div>
                      <div className={`font-bold text-neutral-900 text-sm ${kn ? 'font-kannada' : ''}`}>{item.label}</div>
                      <div className={`text-neutral-600 text-sm whitespace-pre-line mt-1 ${kn ? 'font-kannada' : ''}`}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-2xl overflow-hidden bg-neutral-100 h-48 flex items-center justify-center border border-neutral-200">
                <div className="text-center text-neutral-400">
                  <div className="text-3xl mb-2">🗺️</div>
                  <p className="text-sm">Map — Bengaluru, Karnataka 560073</p>
                </div>
              </div>

              <div className="mt-8">
                <h3 className={`font-bold text-neutral-900 mb-4 text-sm ${kn ? 'font-kannada' : ''}`}>{kn ? 'ನಮ್ಮನ್ನು ಅನುಸರಿಸಿ' : 'Follow Us'}</h3>
                <div className="flex gap-3">
                  {['Facebook', 'Twitter', 'Instagram', 'YouTube', 'LinkedIn'].map((s) => (
                    <a key={s} href="#" className="px-3 py-2 rounded-lg bg-neutral-100 text-neutral-600 text-xs font-medium hover:bg-saffron-pale hover:text-saffron transition-colors">{s}</a>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h2 className={`text-2xl font-bold text-indigo mb-8 ${kn ? 'font-kannada' : ''}`}>{t.contact.sendMessage}</h2>
              {submitted ? (
                <div className="bg-saffron-pale rounded-3xl p-10 text-center">
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className={`text-xl font-bold text-indigo mb-2 ${kn ? 'font-kannada' : ''}`}>{t.contact.successTitle}</h3>
                  <p className={`text-neutral-600 text-sm ${kn ? 'font-kannada' : ''}`}>{t.contact.successMsg}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.contact.name} *</label>
                      <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder={kn ? 'ನಿಮ್ಮ ಹೆಸರು' : 'Your name'}
                        className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.name ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                      {errors.name && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.name}</p>}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.contact.email} *</label>
                      <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com"
                        className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.email ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                      {errors.email && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.email}</p>}
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.contact.phoneLabel}</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="9876543210"
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors" />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.contact.subject}</label>
                    <select value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors bg-white">
                      <option value="">{kn ? 'ವಿಷಯ ಆಯ್ಕೆ ಮಾಡಿ' : 'Select a subject'}</option>
                      <option value="donation">{kn ? 'ದೇಣಿಗೆ ವಿಚಾರಣೆ' : 'Donation Enquiry'}</option>
                      <option value="volunteer">{kn ? 'ಸ್ವಯಂಸೇವಕ' : 'Volunteering'}</option>
                      <option value="partnership">{kn ? 'ಪಾಲುದಾರಿಕೆ / CSR' : 'Partnership / CSR'}</option>
                      <option value="scholarship">{kn ? 'ವಿದ್ಯಾರ್ಥಿವೇತನ ಅರ್ಜಿ' : 'Scholarship Application'}</option>
                      <option value="media">{kn ? 'ಮಾಧ್ಯಮ / ಪ್ರೆಸ್' : 'Media / Press'}</option>
                      <option value="other">{kn ? 'ಇತರ' : 'Other'}</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.contact.message} *</label>
                    <textarea value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} placeholder={kn ? 'ನಾವು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?' : 'How can we help you?'}
                      rows={5} className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors resize-none ${errors.message ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                    {errors.message && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.message}</p>}
                  </div>
                  <button type="submit" className={`w-full py-4 btn-saffron rounded-xl font-bold text-base text-white ${kn ? 'font-kannada' : ''}`}>
                    {t.contact.submitBtn}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
